import React, { useEffect, useState } from "react";
import {
  Navigate,
  NavigateFunction,
  Params,
  Route,
  Routes,
} from "react-router-dom";
import MerchantAccountStore from "./MerchantAccountStore";
import { observer } from "mobx-react";
import {
  AuthService,
  CustomButton,
  FlexContainer,
  Loader,
  SapphireBlueButton,
  SingleSelect,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  CountryStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../../shared/store";
import { SelectedItemModel } from "../../shared/models";
import { DashboardTopBar } from "../../shared/components";
import { DashboardGrid } from "../../shared/styles";
import NoMerchantAccountPlaceholder from "./components/NoMerchantAccountPlaceholder";
import MerchantSignup from "./components/MerchantSignup";

const MerchantApplicationSuccess = React.lazy(
  () => import("./components/MerchantApplicationSuccess"),
);
const MerchantAccountSignUp = React.lazy(
  () => import("./components/MerchantAccountSignUp"),
);
const MerchantAccounts = React.lazy(
  () => import("./components/MerchantAccounts"),
);
const ItemContentArea = React.lazy(
  () => import("../../shared/components/ItemContentArea"),
);
const MerchantAccountDetail = React.lazy(
  () => import("./components/MerchantAccountDetail"),
);

const NMIMerchantAccountSignUp = React.lazy(
  () => import("./components/NMIMerchantAccountSignUp"),
);

interface IProps {
  location: Location;
  navigate: NavigateFunction;
  itemStore: ItemsStore;
  params: Readonly<Params<string>>;
  merchantAccountStore: MerchantAccountStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
  getMerchantAccounts: () => void;
}

const applyOptions = [
  { label: "Fiserve", value: "fiserve" },
  { label: "NMI", value: "nmi" },
];

// TEMP: The "Apply Now" (Fiserv/Echelon) merchant application flow is hidden from
// MindMe until the new merchant application process is in place. For now only the
// "Connect" button is shown (and only when the NMI flag is enabled). Flip this back
// to `true` to restore the Apply flow once the new process is ready.
const SHOW_APPLY_BUTTON = false;

const MerchantAccountRoutes: React.FC<IProps> = ({
  location,
  itemStore,
  navigate,
  merchantAccountStore,
  params,
  rolesAndPermissionStore,
  getMerchantAccounts,
}) => {
  const [selectedProvider, setSelectedProvider] = useState<string>("fiserve");
  const [showApplyDropdown, setShowApplyDropdown] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const dropdown = document.getElementById("apply-now-dropdown");
      if (dropdown && !dropdown.contains(e.target as Node)) {
        setShowApplyDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  function merchantAccountId() {
    const split = ((params && params["*"]) || "").split("/");
    const id = split[split.length - 1];
    if (UtilityMethods.isValidObjectId(id)) {
      return id;
    }
    return null;
  }

  useEffect(() => {
    itemStore?.setBreadCrumbData(
      new SelectedItemModel({
        route: "/app/merchant-accounts",
        title: "",
      }),
    );

    return () => {
      itemStore?.setBreadCrumbData();
    };
  }, []);

  // const handleApplyOptionClick = (value: string) => {
  //   setShowApplyDropdown(false);
  //   if (value === "fiserve") {
  //     navigate("/app/merchant-accounts/create-new");
  //   } else if (value === "nmi") {
  //     navigate("/app/merchant-accounts/create-new/nmi");
  //   }
  // };

  // const singleSelect = (): React.ReactNode => {
  //   return (
  //     <SingleSelect
  //       selectedKey={selectedProvider}
  //       hasError={false}
  //       searchable={false}
  //       nameKeys={["label"]}
  //       valueKeys={["value"]}
  //       options={[providerOptions]}
  //       onChange={(option: any) => handleProviderChange(option.value)}
  //       className="dpdn-no-separator btn-caption-dpdn"
  //     />
  //   );
  // };

  function topBar(
    node: React.ReactNode,
    rightContent: React.ReactNode,
    showNoMerchantAccountNotification?: boolean,
  ) {
    return (
      <>
        <DashboardTopBar
          showNoMerchantAccountNotification={showNoMerchantAccountNotification}
          itemStore={itemStore}
          rightContent={rightContent}
          showRightContent={
            rightContent && showNoMerchantAccountNotification !== true
          }
          rolesAndPermissionStore={rolesAndPermissionStore}
        />
        <ItemContentArea
          itemStore={itemStore}
          node={node}
          onlyShowNotificationBox={showNoMerchantAccountNotification === true}
          showMerchantAccountNotificationBar={showNoMerchantAccountNotification}
        />
      </>
    );
  }

  function renderConnectButton() {
    if (!AuthService.mindmeAccountDetails?.isNMIEnable) {
      return null;
    }
    return (
      <SapphireBlueButton
        height="40px"
        onClick={() => {
          if (
            !rolesAndPermissionStore.hasManageMerchantAccountPermission(
              "Connect Merchant Account",
            )
          ) {
            return;
          }
          navigate(`/app/merchant-accounts/create-new/NMI`);
        }}
      >
        Connect
      </SapphireBlueButton>
    );
  }

  const topRightButton = () => {
    return (
      <div style={{ position: "relative" }} id="apply-now-dropdown">
        <FlexContainer
          alignItems="center"
          spacing={20}
          justifyContent="flex-end"
        >
          {renderConnectButton()}
          {SHOW_APPLY_BUTTON && (
            <CustomButton
              bgColor="#556bb8"
              color="#fff"
              height="40px"
              onClick={() => {
                if (
                  !rolesAndPermissionStore.hasManageMerchantAccountPermission(
                    "Apply for Merchant Account",
                  )
                ) {
                  return;
                }
                navigate(`/app/merchant-accounts/create-new/Fiserv`);
                // setShowApplyDropdown((prev) => !prev);
              }}
            >
              Apply Now
            </CustomButton>
          )}
        </FlexContainer>

        {/* {showApplyDropdown && (
          <div
            style={{
              position: "absolute",
              top: "48px",
              right: 0,
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              borderRadius: "4px",
              zIndex: 1000,
            }}
          >
            {applyOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => handleApplyOptionClick(option.value)}
                style={{
                  padding: "10px 16px",
                  cursor: "pointer",
                  borderBottom: "1px solid #eee",
                }}
                onMouseEnter={(e) =>
                ((e.target as HTMLDivElement).style.backgroundColor =
                  "#f5f5f5")
                }
                onMouseLeave={(e) =>
                  ((e.target as HTMLDivElement).style.backgroundColor = "#fff")
                }
              >
                {option.label}
              </div>
            ))}
          </div>
        )} */}
      </div>
    );
  };

  if (!itemStore.countryStore.countryDataLoaded) {
    return <>{new Loader().loaderComponent}</>;
  }
  return (
    <DashboardGrid>
      <Routes>
        <Route
          path={"/"}
          element={topBar(
            <MerchantAccounts
              merchantAccountStore={merchantAccountStore}
              rolesAndPermissionStore={rolesAndPermissionStore}
              itemStore={itemStore}
            />,
            topRightButton(),
            !merchantAccountStore.isMerchantAccountsLoadingInProgress &&
              merchantAccountStore.merchantAccounts.length === 0,
          )}
        />
        <Route
          path={"/no-merchant"}
          element={
            <div className="full-height merchant-account-plh">
              <NoMerchantAccountPlaceholder
                rolesAndPermissionStore={rolesAndPermissionStore}
              />
            </div>
          }
        />
        {/* <Route
          path={"/choose-gateway"}
          element={topBar(<GatewaySelector onSelect={(gateway) => navigate(`/app/merchant-accounts/create-new/${gateway}`)} />, <></>)}
        /> */}
        {/* <Route
          path={"/create-new"}
          element={topBar(
            <MerchantAccountSignUp
              merchantAccountStore={merchantAccountStore}
              rolesAndPermissionStore={rolesAndPermissionStore}
              itemStore={itemStore}
            />,
            <></>
          )}
        /> */}
        <Route
          path={"/create-new/:gateway"}
          element={topBar(
            <MerchantSignup
              merchantAccountStore={merchantAccountStore}
              rolesAndPermissionStore={rolesAndPermissionStore}
              itemStore={itemStore}
            />,
            <></>,
          )}
        />
        <Route
          path={"/success/:id"}
          element={topBar(
            <MerchantApplicationSuccess
              merchantAccountStore={merchantAccountStore}
              id={merchantAccountId()}
              rolesAndPermissionStore={rolesAndPermissionStore}
              getMerchantAccounts={() => getMerchantAccounts()}
            />,
            topRightButton(),
          )}
        />
        <Route
          path={"/details/:id"}
          element={
            <MerchantAccountDetail
              merchantAccountStore={merchantAccountStore}
              id={merchantAccountId()}
              rolesAndPermissionStore={rolesAndPermissionStore}
              itemStore={itemStore}
            />
          }
        />
        <Route
          path="*"
          element={<Navigate to="/app/merchant-accounts/create-new/Fiserv" />}
        />
      </Routes>
    </DashboardGrid>
  );
};

export default withRouter(observer(MerchantAccountRoutes));
